import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMakeDay, createMakeDay, updateMakeDay, getSuppliers, getSilos, getMilkIntake, getSupplierCodes, suggestSupplierCode } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Loader2, FlaskConical, Droplets, PackageOpen, Boxes } from 'lucide-react';

const UNITS = ['kg', 'litres', 'each', 'box', 'pallet', 'other'];
const empty = {
  make_date: new Date().toISOString().slice(0, 10),
  product_type: '', production_site: '', supervisor: '', notes: '',
  status: 'draft',
  milk_used: [],
  ingredients_used: [],
  packaging_used: [],
  output_batches: [],
};

const emptyMilk = { silo_id: '', milk_intake_id: '', quantity_litres: 0, notes: '' };
const emptyIng = { internal_item: '', supplier_id: '', supplier_product_code: '', supplier_lot: '', quantity: 0, unit: 'kg', notes: '' };
const emptyPkg = { ...emptyIng, unit: 'each' };
const emptyOut = { batch_number: '', product: '', quantity: 0, unit: 'each', notes: '' };

export default function MakeDayEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [silos, setSilos] = useState([]);
  const [intakes, setIntakes] = useState([]);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const [s, sl, mi] = await Promise.all([getSuppliers(), getSilos(), getMilkIntake()]);
      setSuppliers(s.data); setSilos(sl.data); setIntakes(mi.data);
      if (!isNew) {
        const r = await getMakeDay(id);
        setForm({ ...empty, ...r.data });
      }
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  })(); }, [id, isNew]);

  // Auto-suggest supplier code when user picks (supplier, internal_item) on an ing/pkg line
  const autosuggest = async (line, type, i, supplier_id, internal_item) => {
    if (!supplier_id || !internal_item) return;
    try {
      const r = await suggestSupplierCode(supplier_id, internal_item);
      if (r.data?.supplier_product_code) {
        const key = type === 'ing' ? 'ingredients_used' : 'packaging_used';
        setForm(f => ({ ...f, [key]: f[key].map((ln, idx) => idx === i ? { ...ln, supplier_product_code: r.data.supplier_product_code, unit: r.data.unit || ln.unit } : ln) }));
      }
    } catch (_) {}
  };

  const upd = (key, fn) => setForm(f => ({ ...f, [key]: fn(f[key]) }));

  const save = async () => {
    if (!form.make_date) { toast.error('Make date is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        milk_used: form.milk_used.map(m => ({ ...m, quantity_litres: Number(m.quantity_litres) || 0 })),
        ingredients_used: form.ingredients_used.map(m => ({ ...m, quantity: Number(m.quantity) || 0 })),
        packaging_used: form.packaging_used.map(m => ({ ...m, quantity: Number(m.quantity) || 0 })),
        output_batches: form.output_batches.map(m => ({ ...m, quantity: Number(m.quantity) || 0 })),
      };
      if (isNew) {
        const r = await createMakeDay(payload);
        toast.success(`Make day ${r.data.make_day_ref} created`);
        navigate('/production/make-days');
      } else {
        await updateMakeDay(id, payload);
        toast.success('Make day saved');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const supName = (id) => suppliers.find(s => s.id === id)?.name || '—';
  const supOpts = suppliers;
  const intakeOpts = intakes;

  return (
    <div className="space-y-4" data-testid="make-day-edit-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/production/make-days')}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2"><FlaskConical className="w-5 h-5" />{isNew ? 'New Make Day' : `Edit ${form.make_day_ref || ''}`}</h1>
            <p className="text-sm text-muted-foreground">Record milk, ingredients, packaging used and the cheese batches produced. Keystone derives the full trace from this one screen.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="btn-save-makeday">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}<Save className="w-4 h-4 mr-1" />Save</Button>
      </div>

      {/* Header */}
      <Card><CardContent className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label>Make date *</Label><Input type="date" value={form.make_date} onChange={e => setForm({ ...form, make_date: e.target.value })} data-testid="md-make-date" /></div>
        <div><Label>Product</Label><Input value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })} placeholder="Tipperary Blue 200g" /></div>
        <div><Label>Production site</Label><Input value={form.production_site} onChange={e => setForm({ ...form, production_site: e.target.value })} /></div>
        <div><Label>Supervisor</Label><Input value={form.supervisor} onChange={e => setForm({ ...form, supervisor: e.target.value })} /></div>
        <div><Label>Status</Label>
          <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="locked">Locked</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3"><Label>Notes</Label><Textarea rows={1} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
      </CardContent></Card>

      {/* Milk used */}
      <Card><CardContent className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><Droplets className="w-4 h-4" />Milk used</h3>
          <Button size="sm" variant="outline" onClick={() => upd('milk_used', a => [...a, { ...emptyMilk }])}><Plus className="w-4 h-4 mr-1" />Add milk line</Button>
        </div>
        {form.milk_used.length === 0 ? <div className="text-sm text-muted-foreground italic">No milk lines. Add at least one to enable backward trace.</div> :
          <div className="space-y-2">
            {form.milk_used.map((ln, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 border rounded">
                <div className="col-span-4"><Label className="text-xs">Silo</Label>
                  <Select value={ln.silo_id || ''} onValueChange={v => upd('milk_used', a => a.map((x, idx) => idx === i ? { ...x, silo_id: v } : x))}>
                    <SelectTrigger><SelectValue placeholder="Silo…" /></SelectTrigger>
                    <SelectContent>{silos.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-5"><Label className="text-xs">Linked milk intake</Label>
                  <Select value={ln.milk_intake_id || ''} onValueChange={v => upd('milk_used', a => a.map((x, idx) => idx === i ? { ...x, milk_intake_id: v } : x))}>
                    <SelectTrigger><SelectValue placeholder="Intake…" /></SelectTrigger>
                    <SelectContent>{intakeOpts.map(ix => <SelectItem key={ix.id} value={ix.id}>{ix.intake_ref} · {ix.volume_litres}L · {supName(ix.supplier_id)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-xs">Litres used</Label><Input type="number" value={ln.quantity_litres} onChange={e => upd('milk_used', a => a.map((x, idx) => idx === i ? { ...x, quantity_litres: e.target.value } : x))} /></div>
                <div className="col-span-1 text-right"><Button size="sm" variant="ghost" onClick={() => upd('milk_used', a => a.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-red-600" /></Button></div>
              </div>
            ))}
          </div>}
      </CardContent></Card>

      {/* Ingredient + Packaging lines (same shape) */}
      {[
        { key: 'ingredients_used', label: 'Ingredients used', icon: PackageOpen, emptyRow: emptyIng, lineType: 'ing' },
        { key: 'packaging_used', label: 'Packaging used', icon: PackageOpen, emptyRow: emptyPkg, lineType: 'pkg' },
      ].map(sec => (
        <Card key={sec.key}><CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2"><sec.icon className="w-4 h-4" />{sec.label}</h3>
            <Button size="sm" variant="outline" onClick={() => upd(sec.key, a => [...a, { ...sec.emptyRow }])}><Plus className="w-4 h-4 mr-1" />Add line</Button>
          </div>
          {form[sec.key].length === 0 ? <div className="text-sm text-muted-foreground italic">No lines yet.</div> :
            <div className="space-y-2">
              {form[sec.key].map((ln, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 border rounded">
                  <div className="col-span-3"><Label className="text-xs">Internal item</Label><Input value={ln.internal_item}
                    onChange={e => upd(sec.key, a => a.map((x, idx) => idx === i ? { ...x, internal_item: e.target.value } : x))}
                    onBlur={e => autosuggest(ln, sec.lineType, i, ln.supplier_id, e.target.value)} placeholder="Cheese culture" /></div>
                  <div className="col-span-3"><Label className="text-xs">Supplier</Label>
                    <Select value={ln.supplier_id || ''} onValueChange={v => {
                      upd(sec.key, a => a.map((x, idx) => idx === i ? { ...x, supplier_id: v } : x));
                      autosuggest(ln, sec.lineType, i, v, ln.internal_item);
                    }}>
                      <SelectTrigger><SelectValue placeholder="Supplier…" /></SelectTrigger>
                      <SelectContent>{supOpts.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label className="text-xs">Supplier lot *</Label><Input value={ln.supplier_lot} onChange={e => upd(sec.key, a => a.map((x, idx) => idx === i ? { ...x, supplier_lot: e.target.value } : x))} placeholder="LOT-123" /></div>
                  <div className="col-span-2"><Label className="text-xs">Sup. code</Label><Input value={ln.supplier_product_code} onChange={e => upd(sec.key, a => a.map((x, idx) => idx === i ? { ...x, supplier_product_code: e.target.value } : x))} /></div>
                  <div className="col-span-1"><Label className="text-xs">Qty</Label><Input type="number" value={ln.quantity} onChange={e => upd(sec.key, a => a.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))} /></div>
                  <div className="col-span-1"><Label className="text-xs">Unit</Label>
                    <Select value={ln.unit} onValueChange={v => upd(sec.key, a => a.map((x, idx) => idx === i ? { ...x, unit: v } : x))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-12 text-right -mt-2"><Button size="sm" variant="ghost" onClick={() => upd(sec.key, a => a.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-red-600" /></Button></div>
                </div>
              ))}
            </div>}
        </CardContent></Card>
      ))}

      {/* Output batches */}
      <Card><CardContent className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><Boxes className="w-4 h-4" />Output cheese batches</h3>
          <Button size="sm" variant="outline" onClick={() => upd('output_batches', a => [...a, { ...emptyOut }])}><Plus className="w-4 h-4 mr-1" />Add batch</Button>
        </div>
        {form.output_batches.length === 0 ? <div className="text-sm text-muted-foreground italic">No output batches yet.</div> :
          <div className="space-y-2">
            {form.output_batches.map((ln, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 border rounded bg-emerald-50/50">
                <div className="col-span-3"><Label className="text-xs">Batch number *</Label><Input value={ln.batch_number} onChange={e => upd('output_batches', a => a.map((x, idx) => idx === i ? { ...x, batch_number: e.target.value } : x))} placeholder="CB-2026-014" /></div>
                <div className="col-span-4"><Label className="text-xs">Product</Label><Input value={ln.product} onChange={e => upd('output_batches', a => a.map((x, idx) => idx === i ? { ...x, product: e.target.value } : x))} /></div>
                <div className="col-span-2"><Label className="text-xs">Quantity</Label><Input type="number" value={ln.quantity} onChange={e => upd('output_batches', a => a.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))} /></div>
                <div className="col-span-2"><Label className="text-xs">Unit</Label>
                  <Select value={ln.unit} onValueChange={v => upd('output_batches', a => a.map((x, idx) => idx === i ? { ...x, unit: v } : x))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-1 text-right"><Button size="sm" variant="ghost" onClick={() => upd('output_batches', a => a.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-red-600" /></Button></div>
              </div>
            ))}
          </div>}
      </CardContent></Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} size="lg" data-testid="btn-save-makeday-bottom">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}<Save className="w-4 h-4 mr-1" />Save Make Day</Button>
      </div>
    </div>
  );
}
